<template>
  <!-- `aria-busy` plutôt qu'un contenu remplacé par un sablier : le lecteur d'écran annonce
       l'attente sans que la structure de la page disparaisse (RGAA, cf. design-system-dashboards). -->
  <div class="learning-page" :aria-busy="loading ? 'true' : 'false'">
    <header-section :learner="learner" />

    <!-- L'échec de lecture se dit. L'écran affichait auparavant une progression littérale : il ne
         pouvait pas échouer, donc il n'avait rien à annoncer. -->
    <p v-if="error" class="load-error" role="alert">
      Vos apprentissages n'ont pas pu être chargés. Rechargez la page pour réessayer.
    </p>

    <div class="learning-grid">
      <sidebar-section :learner="learner" />

      <main class="learning-main">
        <live-challenge-banner v-if="challenge" :challenge="challenge" />

        <pillar-section
          v-for="pillar in pillars"
          :key="pillar.code"
          :pillar="pillar"
          :accepting-action-id="acceptingActionId"
          @accept="accept"
        />
      </main>
    </div>
  </div>
</template>

<script>
import HeaderSection from "./HeaderSection.vue"
import SidebarSection from "./SidebarSection.vue"
import LiveChallengeBanner from "./LiveChallengeBanner.vue"
import PillarSection from "./PillarSection.vue"

/**
 * Écran d'apprentissage du convive : trois piliers, sa progression, le défi en cours.
 *
 * Tout ce que l'écran affichait était en dur — identité, niveau, XP, six badges, deux piliers
 * entiers — sauf le premier pilier, lu par un `fetch` local sur le catalogue. Un écran qui invente
 * sa progression annonce au convive une avance qu'il n'a pas.
 *
 * Une seule lecture (`fetchPedagogicalBoard`) rend l'ensemble : niveau, compteurs de pilier et
 * cartes viennent ainsi du même instant. Les composer par trois appels laisserait le compteur d'un
 * pilier parler d'un état que les cartes ne montrent plus.
 *
 * La navigation n'appartient pas à l'écran : elle est celle du shell Labricot (§9.3). Le faux menu
 * de la colonne de gauche — des `<li>` non interactifs annonçant six destinations — a été retiré.
 */
export default {
  name: "NutriQuest",

  components: {
    HeaderSection,
    SidebarSection,
    LiveChallengeBanner,
    PillarSection,
  },

  data() {
    return {
      board: null,
      loading: false,
      error: false,
      // Activité dont l'acceptation est en cours. Portée à la carte et non à l'écran : verrouiller
      // tout l'écran pour une inscription empêcherait de lire le reste.
      acceptingActionId: null,
    }
  },

  computed: {
    learner() {
      return this.board ? this.board.learner : null
    },
    pillars() {
      return this.board ? this.board.pillars : []
    },
    challenge() {
      return this.board ? this.board.challenge : null
    },
  },

  methods: {
    load() {
      this.loading = true
      this.error = false
      return this.$store
        .dispatch("fetchPedagogicalBoard")
        .then((board) => {
          this.board = board
        })
        .catch(() => {
          this.error = true
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Accepter une suggestion, puis relire le tableau.
     *
     * Relire plutôt que retoucher la carte localement : l'inscription change l'état de la carte,
     * son avancement et le compteur de son pilier. Les recalculer côté client les ferait diverger
     * de ce que le registre a réellement enregistré.
     */
    accept(actionId) {
      this.acceptingActionId = actionId
      return this.$store
        .dispatch("engageInAction", { studyId: actionId })
        .then(() => this.load())
        .catch(() => {
          this.error = true
        })
        .finally(() => {
          this.acceptingActionId = null
        })
    },
  },

  mounted() {
    this.load()
  },
}
</script>

<style scoped lang="scss">
@import "../../scss/nutriquest";

/* Cadre commun aux dashboards Labricot : même largeur utile, même fond, même gouttière.
   `text-align` est explicite parce que le shell centre par défaut : sans lui, chaque paragraphe de
   carte se centrait, y compris les libellés d'état et les listes de conditions. */
.learning-page {
  background: $nq-page;
  margin: 0 auto;
  max-width: 1201px;
  min-height: 100vh;
  padding: 1.5rem;
  text-align: left;
}

.learning-grid {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 1.5rem;
  align-items: start;
}

.learning-main {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

.load-error {
  background: #fef2f2;
  border: 1px solid #dc2626;
  border-radius: 8px;
  color: $nq-state-alert-ink;
  margin: 0 0 1.5rem 0;
  padding: 0.75rem 1rem;
}

@media (max-width: 900px) {
  .learning-grid {
    grid-template-columns: 1fr;
  }
}
</style>
